// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {useContextMenuHoverState} from '@app/features/app/hooks/useContextMenuHoverState';
import styles from '@app/features/channel/components/MessageActionBar.module.css';
import {
	createMessageActionHandlers,
	getEffectiveContent,
	isClientSystemMessage,
	isEmbedsSuppressed,
	useMessagePermissions,
} from '@app/features/channel/components/MessageActionUtils';
import Channels from '@app/features/channel/state/Channels';
import {MessageDebugModal} from '@app/features/devtools/components/debug/MessageDebugModal';
import {EmojiPickerPopout} from '@app/features/emoji/components/popouts/EmojiPickerPopout';
import Emoji from '@app/features/emoji/state/Emoji';
import EmojiPicker from '@app/features/emoji/state/EmojiPicker';
import type {FlatEmoji} from '@app/features/emoji/types/EmojiTypes';
import {shouldUseNativeEmoji} from '@app/features/expressions/utils/EmojiUtils';
import {filterEmojisForAutocomplete} from '@app/features/expressions/utils/ExpressionPermissionUtils';
import {getEmojiDisplayData} from '@app/features/expressions/utils/SkinToneUtils';
import type {Message} from '@app/features/messaging/models/Message';
import SavedMessages from '@app/features/messaging/state/SavedMessages';
import {ComponentDispatch} from '@app/features/platform/utils/ComponentBus';
import messageStyles from '@app/features/theme/styles/Message.module.css';
import {
	AddReactionIcon,
	BookmarkIcon,
	CopyIdIcon,
	CopyLinkIcon,
	CopyMessageTextIcon,
	DebugMessageIcon,
	DeleteIcon,
	EditMessageIcon,
	ForwardIcon,
	MarkAsUnreadIcon,
	MoreIcon,
	PinIcon,
	ReplyIcon,
	RetryIcon,
	SuppressEmbedsIcon,
} from '@app/features/ui/action_menu/ContextMenuIcons';
import {MessageContextMenu} from '@app/features/ui/action_menu/MessageContextMenu';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as PopoutCommands from '@app/features/ui/commands/PopoutCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Popout} from '@app/features/ui/popover/Popout';
import ContextMenu from '@app/features/ui/state/ContextMenu';
import KeyboardMode from '@app/features/ui/state/KeyboardMode';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {canUseWindowFocusedActivationClick} from '@app/features/ui/utils/WindowFocusInteractionGuard';
import UserSettings from '@app/features/user/state/UserSettings';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {MessageStates} from '@app/vendor/constants/ChannelConstants';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {autorun} from 'mobx';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react';

const shiftKeyManager = (() => {
	let isShiftPressed = false;
	const listeners = new Set<() => void>();
	let notifyTimeout: NodeJS.Timeout | null = null;
	const scheduleNotify = () => {
		if (notifyTimeout) return;
		notifyTimeout = setTimeout(() => {
			notifyTimeout = null;
			listeners.forEach((listener) => listener());
		}, 0);
	};
	const setShiftPressed = (pressed: boolean) => {
		if (isShiftPressed !== pressed) {
			isShiftPressed = pressed;
			scheduleNotify();
		}
	};
	const handleKeyDown = (event: KeyboardEvent) => {
		if (event.key === 'Shift') {
			setShiftPressed(true);
		}
	};
	const handleKeyUp = (event: KeyboardEvent) => {
		if (event.key === 'Shift') {
			setShiftPressed(false);
		}
	};
	const handleWindowBlur = () => {
		setShiftPressed(false);
	};
	window.addEventListener('keydown', handleKeyDown);
	window.addEventListener('keyup', handleKeyUp);
	window.addEventListener('blur', handleWindowBlur);
	return {
		subscribe: (listener: () => void) => {
			listeners.add(listener);
			return () => listeners.delete(listener);
		},
		getSnapshot: () => isShiftPressed,
		getServerSnapshot: () => false,
	};
})();
const quickReactionManager = (() => {
	let cache: Array<FlatEmoji> | null = null;
	const listeners = new Set<() => void>();
	const recompute = () => {
		const allEmojis = Emoji.getAllEmojis(null);
		cache = EmojiPicker.getQuickReactionEmojis(allEmojis, 3);
		listeners.forEach((listener) => listener());
	};
	autorun(() => {
		const usage = EmojiPicker.emojiUsage;
		for (const key in usage) {
			const entry = usage[key];
			entry?.count;
			entry?.lastUsed;
		}
		recompute();
	});
	ComponentDispatch.subscribe('EMOJI_PICKER_RERENDER', recompute);
	return {
		subscribe(listener: () => void) {
			listeners.add(listener);
			return () => listeners.delete(listener);
		},
		getSnapshot(): Array<FlatEmoji> {
			if (!cache) {
				recompute();
			}
			return cache ?? [];
		},
		getServerSnapshot(): Array<FlatEmoji> {
			return [];
		},
	};
})();
const useShiftKey = (enabled: boolean) => {
	const subscribe = useCallback(
		(listener: () => void) => {
			if (!enabled) {
				return () => undefined;
			}
			return shiftKeyManager.subscribe(listener);
		},
		[enabled],
	);
	const getSnapshot = useCallback(() => {
		return enabled ? shiftKeyManager.getSnapshot() : false;
	}, [enabled]);
	return useSyncExternalStore(subscribe, getSnapshot, shiftKeyManager.getServerSnapshot);
};
const suppressBlockedActivationClick = (event: React.SyntheticEvent): boolean => {
	if (canUseWindowFocusedActivationClick()) {
		return false;
	}
	event.preventDefault();
	event.stopPropagation();
	return true;
};
interface MessageActionBarButtonProps {
	label: string;
	icon: React.ReactNode;
	onClick?: (event: React.MouseEvent | React.KeyboardEvent) => void;
	onPointerDownCapture?: (event: React.PointerEvent) => void;
	danger?: boolean;
	isActive?: boolean;
	dataAction?: string;
}
const MessageActionBarButton = React.forwardRef<HTMLButtonElement, MessageActionBarButtonProps>(
	({label, icon, onClick, onPointerDownCapture, danger, isActive, dataAction}, ref) => {
		const handleClick = useCallback(
			(event: React.MouseEvent | React.KeyboardEvent) => {
				if (suppressBlockedActivationClick(event)) {
					return;
				}
				event.preventDefault();
				event.stopPropagation();
				onClick?.(event);
			},
			[onClick],
		);
		const handlePointerDownCapture = useCallback(
			(event: React.PointerEvent) => {
				if (suppressBlockedActivationClick(event)) {
					return;
				}
				onPointerDownCapture?.(event);
			},
			[onPointerDownCapture],
		);
		const buttonClassName = useMemo(
			() => clsx(styles.button, danger && styles.danger, isActive && styles.active),
			[danger, isActive],
		);
		return (
			<Tooltip text={label}>
				<FocusRing>
					<button
						type="button"
						ref={ref}
						aria-label={label}
						onClick={handleClick}
						onPointerDownCapture={handlePointerDownCapture}
						className={buttonClassName}
						data-action={dataAction}
					>
						<div className={styles.actionBarIcon}>{icon}</div>
					</button>
				</FocusRing>
			</Tooltip>
		);
	},
);

MessageActionBarButton.displayName = 'MessageActionBarButton';
interface QuickReactionButtonProps {
	emoji: FlatEmoji;
	onReact: (emoji: FlatEmoji) => void;
}
const QuickReactionButton = observer(
	React.forwardRef<HTMLButtonElement, QuickReactionButtonProps>(({emoji, onReact}, ref) => {
		const {t} = useLingui();
		const [isHovered, setIsHovered] = useState(false);
		const {surrogates: displaySurrogates, url: displayUrl} = getEmojiDisplayData(emoji);
		const handleClick = useCallback(
			(event: React.MouseEvent | React.KeyboardEvent) => {
				if (suppressBlockedActivationClick(event)) {
					return;
				}
				event.preventDefault();
				event.stopPropagation();
				onReact(emoji);
			},
			[emoji, onReact],
		);
		const handleMouseEnter = useCallback(() => setIsHovered(true), []);
		const handleMouseLeave = useCallback(() => setIsHovered(false), []);
		const emojiNameWithColons = useMemo(() => `:${emoji.name}:`, [emoji.name]);
		const isUnicodeEmoji = useMemo(() => !emoji.guildId && !emoji.id, [emoji.guildId, emoji.id]);
		const useNativeRendering = useMemo(() => shouldUseNativeEmoji && isUnicodeEmoji, [isUnicodeEmoji]);
		const shouldShowAnimated = useMemo(() => emoji.animated && isHovered, [emoji.animated, isHovered]);
		const emojiSrc = useMemo(
			() =>
				emoji.animated && emoji.id && !useNativeRendering
					? AvatarUtils.getEmojiURL({id: emoji.id, animated: shouldShowAnimated})
					: (displayUrl ?? ''),
			[emoji.animated, emoji.id, displayUrl, useNativeRendering, shouldShowAnimated],
		);
		const tooltipContent = useCallback(
			() => (
				<div className={styles.tooltipContent}>
					<span>{emojiNameWithColons}</span>
					<span className={styles.tooltipHint}>{t`Click to react`}</span>
				</div>
			),
			[emojiNameWithColons, t],
		);
		const ariaLabel = useMemo(() => `React with ${emojiNameWithColons}`, [emojiNameWithColons]);
		return (
			<Tooltip text={tooltipContent}>
				<FocusRing>
					<button
						type="button"
						ref={ref}
						aria-label={ariaLabel}
						onClick={handleClick}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
						className={styles.button}
					>
						{useNativeRendering ? (
							<span className={styles.emojiImage}>{displaySurrogates}</span>
						) : (
							<img src={emojiSrc} alt={emoji.name} className={styles.emojiImage} />
						)}
					</button>
				</FocusRing>
			</Tooltip>
		);
	}),
);

QuickReactionButton.displayName = 'QuickReactionButton';
interface MessageActionBarCoreProps {
	message: Message;
	handleDelete: (bypassConfirm?: boolean) => void;
	permissions: {
		canSendMessages: boolean;
		canAddReactions: boolean;
		canEditMessage: boolean;
		canDeleteMessage: boolean;
		canPinMessage: boolean;
		shouldRenderSuppressEmbeds: boolean;
	};
	isSaved: boolean;
	developerMode: boolean;
	isHovering?: boolean;
	onPopoutToggle?: (isOpen: boolean) => void;
}
export const MessageActionBarCore: React.FC<MessageActionBarCoreProps> = observer(
	({message, handleDelete, permissions, isSaved, developerMode, isHovering = false, onPopoutToggle}) => {
		const {t, i18n} = useLingui();
		const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
		const moreOptionsButtonRef = useRef<HTMLButtonElement>(null);
		const emojiPickerButtonRef = useRef<HTMLButtonElement>(null);
		const actionBarRef = useRef<HTMLDivElement>(null);
		const contextMenuOpen = useContextMenuHoverState(actionBarRef);
		const moreMenuOpen = useContextMenuHoverState(moreOptionsButtonRef);
		const showMessageActionBar = Accessibility.showMessageActionBar;
		const showQuickReactions = Accessibility.showMessageActionBarQuickReactions;
		const showShiftExpand = Accessibility.showMessageActionBarShiftExpand;
		const onlyMoreButton = Accessibility.showMessageActionBarOnlyMoreButton;
		const keyboardModeEnabled = KeyboardMode.keyboardModeEnabled;
		const isActionBarActive = isHovering || contextMenuOpen || emojiPickerOpen || moreMenuOpen;
		const shouldListenForShift =
			showShiftExpand && showMessageActionBar && !onlyMoreButton && isActionBarActive && !keyboardModeEnabled;
		const shiftPressed = useShiftKey(shouldListenForShift);
		const showFullActions = showShiftExpand && shiftPressed;
		const {
			canSendMessages,
			canAddReactions,
			canEditMessage,
			canDeleteMessage,
			canPinMessage,
			shouldRenderSuppressEmbeds,
		} = permissions;
		const supportsInteractiveActions = useMemo(() => !isClientSystemMessage(message), [message]);
		const handlers = useMemo(() => createMessageActionHandlers(message, {i18n}), [message, i18n]);
		const unfilteredQuickReactionEmojis = useSyncExternalStore(
			quickReactionManager.subscribe,
			quickReactionManager.getSnapshot,
			quickReactionManager.getServerSnapshot,
		);
		const channel = useMemo(() => Channels.getChannel(message.channelId) ?? null, [message.channelId]);
		const quickReactionEmojis = useMemo(
			() => filterEmojisForAutocomplete(i18n, unfilteredQuickReactionEmojis, channel),
			[i18n, unfilteredQuickReactionEmojis, channel],
		);
		const blurEmojiPickerTrigger = useCallback(() => {
			if (keyboardModeEnabled) {
				return;
			}
			requestAnimationFrame(() => emojiPickerButtonRef.current?.blur());
		}, [keyboardModeEnabled]);
		const handleEmojiPickerToggle = useCallback(
			(open: boolean) => {
				setEmojiPickerOpen(open);
				onPopoutToggle?.(open);
				if (!open) {
					blurEmojiPickerTrigger();
				}
			},
			[onPopoutToggle, blurEmojiPickerTrigger],
		);
		const handleEmojiPickerOpen = useCallback(() => handleEmojiPickerToggle(true), [handleEmojiPickerToggle]);
		const handleEmojiPickerClose = useCallback(() => handleEmojiPickerToggle(false), [handleEmojiPickerToggle]);
		useEffect(() => {
			return () => {
				if (emojiPickerOpen) {
					onPopoutToggle?.(false);
				}
			};
		}, [emojiPickerOpen, onPopoutToggle]);
		const handleDebugClick = useCallback(() => {
			ModalCommands.push(modal(() => <MessageDebugModal title={t`Message Debug`} message={message} />));
		}, [message, t]);
		useEffect(() => {
			const unsubscribe = ComponentDispatch.subscribe('EMOJI_PICKER_OPEN', (payload?: unknown) => {
				const data = (payload ?? {}) as {messageId?: string};
				if (data.messageId === message.id && emojiPickerButtonRef.current) {
					PopoutCommands.open({
						key: `emoji_picker-${message.id}`,
						position: 'left-start',
						render: ({onClose}) => (
							<EmojiPickerPopout
								channelId={message.channelId}
								handleSelect={handlers.handleEmojiSelect}
								onClose={onClose}
							/>
						),
						target: emojiPickerButtonRef.current,
						shouldAutoUpdate: false,
						animationType: 'none',
						onOpen: () => handleEmojiPickerToggle(true),
						onClose: () => handleEmojiPickerToggle(false),
					});
				}
			});
			return () => unsubscribe();
		}, [message.id, message.channelId, handlers.handleEmojiSelect, handleEmojiPickerToggle]);
		const handleMoreOptionsPointerDown = useCallback((event: React.PointerEvent) => {
			const contextMenu = ContextMenu.contextMenu;
			const isOpen = !!contextMenu && contextMenu.target.target === moreOptionsButtonRef.current;
			if (isOpen) {
				event.stopPropagation();
				event.preventDefault();
				ContextMenuCommands.close();
			}
		}, []);
		const openMoreOptionsMenu = useCallback(
			(event: React.MouseEvent | React.KeyboardEvent) => {
				if (!showMessageActionBar) {
					return;
				}
				const contextMenu = ContextMenu.contextMenu;
				const isOpen = !!contextMenu && contextMenu.target.target === event.currentTarget;
				if (isOpen) {
					return;
				}
				if (!(event.nativeEvent instanceof MouseEvent)) {
					return;
				}
				ContextMenuCommands.openFromEvent(event as React.MouseEvent, (props) => (
					<MessageContextMenu message={message} onClose={props.onClose} onDelete={handleDelete} />
				));
			},
			[handleDelete, message, showMessageActionBar],
		);
		return (
			<div
				ref={actionBarRef}
				className={clsx(
					styles.actionBarContainer,
					messageStyles.buttons,
					(emojiPickerOpen || contextMenuOpen) && messageStyles.emojiPickerOpen,
				)}
			>
				<div className={styles.actionBar}>
					{message.state === MessageStates.SENT &&
						(onlyMoreButton ? (
							<MessageActionBarButton
								ref={moreOptionsButtonRef}
								icon={<MoreIcon size={20} />}
								label={t`More`}
								onPointerDownCapture={handleMoreOptionsPointerDown}
								onClick={openMoreOptionsMenu}
								isActive={moreMenuOpen}
							/>
						) : (
							<>
								{!showFullActions &&
									canAddReactions &&
									showQuickReactions &&
									quickReactionEmojis.map((emoji) => (
										<QuickReactionButton key={emoji.name} emoji={emoji} onReact={handlers.handleEmojiSelect} />
									))}
								{showFullActions && (
									<>
										{developerMode && (
											<MessageActionBarButton
												icon={<DebugMessageIcon size={20} />}
												label={t`Debug Message`}
												onClick={handleDebugClick}
											/>
										)}
										<MessageActionBarButton
											icon={<CopyIdIcon size={20} />}
											label={t`Copy Message ID`}
											onClick={handlers.handleCopyMessageId}
										/>
										{supportsInteractiveActions && (
											<MessageActionBarButton
												icon={<CopyLinkIcon size={20} />}
												label={t`Copy Message Link`}
												onClick={handlers.handleCopyMessageLink}
											/>
										)}
										{getEffectiveContent(message) && (
											<MessageActionBarButton
												icon={<CopyMessageTextIcon size={20} />}
												label={t`Copy Text`}
												onClick={handlers.handleCopyMessage}
											/>
										)}
										{shouldRenderSuppressEmbeds && (
											<MessageActionBarButton
												icon={<SuppressEmbedsIcon size={20} />}
												label={isEmbedsSuppressed(message) ? t`Unsuppress Embeds` : t`Suppress Embeds`}
												onClick={handlers.handleToggleSuppressEmbeds}
											/>
										)}
										<MessageActionBarButton
											icon={<MarkAsUnreadIcon size={20} />}
											label={t`Mark as Unread`}
											onClick={handlers.handleMarkAsUnread}
										/>
										{message.isUserMessage() && supportsInteractiveActions && (
											<MessageActionBarButton
												icon={<BookmarkIcon size={20} filled={isSaved} />}
												label={isSaved ? t`Remove Bookmark` : t`Bookmark Message`}
												onClick={handlers.handleSaveMessage(isSaved)}
											/>
										)}
										{message.isUserMessage() && canPinMessage && (
											<MessageActionBarButton
												icon={<PinIcon size={20} />}
												label={message.pinned ? t`Unpin Message` : t`Pin Message`}
												onClick={handlers.handlePinMessage}
											/>
										)}
									</>
								)}
								{canAddReactions && (
									<Popout
										render={({onClose}) => (
											<EmojiPickerPopout
												channelId={message.channelId}
												handleSelect={handlers.handleEmojiSelect}
												onClose={onClose}
											/>
										)}
										position="left-start"
										uniqueId={`emoji_picker-actionbar-${message.id}`}
										shouldAutoUpdate={false}
										animationType="none"
										shouldOpenOnClick={() => canUseWindowFocusedActivationClick()}
										onOpen={handleEmojiPickerOpen}
										onClose={handleEmojiPickerClose}
									>
										<MessageActionBarButton
											ref={emojiPickerButtonRef}
											icon={<AddReactionIcon size={20} />}
											label={t`Add Reaction`}
											isActive={emojiPickerOpen}
											dataAction="message-add-reaction-button"
										/>
									</Popout>
								)}
								{message.isUserMessage() && !message.messageSnapshots && canEditMessage && (
									<MessageActionBarButton
										icon={<EditMessageIcon size={20} />}
										label={t`Edit Message`}
										onClick={handlers.handleEditMessage}
									/>
								)}
								{message.isUserMessage() && supportsInteractiveActions && canSendMessages && (
									<MessageActionBarButton
										icon={<ReplyIcon size={20} />}
										label={t`Reply`}
										onClick={handlers.handleReply}
									/>
								)}
								{message.isUserMessage() && supportsInteractiveActions && (
									<MessageActionBarButton
										icon={<ForwardIcon size={20} />}
										label={t`Forward`}
										onClick={handlers.handleForward}
									/>
								)}
								{(!showFullActions || !canDeleteMessage) && (
									<MessageActionBarButton
										ref={moreOptionsButtonRef}
										icon={<MoreIcon size={20} />}
										label={t`More`}
										onPointerDownCapture={handleMoreOptionsPointerDown}
										onClick={openMoreOptionsMenu}
										isActive={moreMenuOpen}
									/>
								)}
								{showFullActions && canDeleteMessage && (
									<MessageActionBarButton
										danger={true}
										icon={<DeleteIcon size={20} />}
										label={t`Delete Message`}
										onClick={(event) => handleDelete(event.shiftKey)}
									/>
								)}
							</>
						))}
					{message.state === MessageStates.FAILED && (
						<>
							<MessageActionBarButton
								icon={<RetryIcon size={20} />}
								label={t`Retry`}
								onClick={handlers.handleRetryMessage}
							/>
							<MessageActionBarButton
								danger={true}
								icon={<DeleteIcon size={20} />}
								label={t`Delete Message`}
								onClick={handlers.handleFailedMessageDelete}
							/>
						</>
					)}
				</div>
			</div>
		);
	},
);
export const MessageActionBar = observer(
	({
		message,
		handleDelete,
		isHovering = false,
		onPopoutToggle,
	}: {
		message: Message;
		handleDelete: (bypassConfirm?: boolean) => void;
		isHovering?: boolean;
		onPopoutToggle?: (isOpen: boolean) => void;
	}) => {
		const isSaved = SavedMessages.isSaved(message.id);
		const developerMode = UserSettings.developerMode;
		const permissions = useMessagePermissions(message);
		if (!permissions) {
			return null;
		}
		return (
			<MessageActionBarCore
				message={message}
				handleDelete={handleDelete}
				permissions={permissions}
				isSaved={isSaved}
				developerMode={developerMode}
				isHovering={isHovering}
				onPopoutToggle={onPopoutToggle}
			/>
		);
	},
);
